/**
 * Pure H OS · Phase D Wave 2 · Unified Timeline.
 * Single chronological feed of every cross-module event.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { LinkedRefChip } from "../components/LinkedRefChip";
import { TIMELINE, TimelineEvent, TimelineKind } from "../mocks/timelineData";

const KIND_LABEL: Record<TimelineKind, string> = {
  decision: "Decision", batch: "Batch", campaign: "Campaign",
  milestone: "Milestone", payment: "Payment", order: "Order",
  supplier: "Supplier", hire: "Hire", insight: "Insight",
};

type Tab = "all" | "high" | TimelineKind;

export default function Timeline() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return TIMELINE;
    if (tab === "high") return TIMELINE.filter((e) => e.severity === "high");
    return TIMELINE.filter((e) => e.kind === tab);
  }, [tab]);

  const current = openId ? TIMELINE.find((e) => e.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="timeline-page">
      <Card testId="tl-filters">
        <TabBar<Tab>
          testId="tl-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${TIMELINE.length}` },
            { key: "high",      label: "High" },
            { key: "decision",  label: "Decisions" },
            { key: "milestone", label: "Milestones" },
            { key: "batch",     label: "Batches" },
            { key: "campaign",  label: "Campaigns" },
            { key: "payment",   label: "Payments" },
            { key: "insight",   label: "Insights" },
          ]}
        />
      </Card>

      <Card padded={false} testId="tl-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Unified Timeline" title={`${filtered.length} event${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
            Every cross-module event, most recent first.
          </div>
        </div>
        <div className="p-2">
          {filtered.map((e) => (
            <ListRow key={e.id} testId={`tl-${e.id}`}
              onClick={() => setOpenId(e.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                    {KIND_LABEL[e.kind]}
                  </span>
                  <span>{e.event}</span>
                </span>
              }
              sublabel={<>{e.module} · {e.actor} · {e.whenRelative}</>}
              trailing={<SeverityBadge severity={e.severity} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="tl-drawer" title={current ? current.event : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${current.module} · ${KIND_LABEL[current.kind]}`} title={current.actor} />
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>{current.whenRelative}</div>
            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>{current.detail}</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Severity</div>
                <div className="mt-2"><SeverityBadge severity={current.severity} /></div>
              </div>
              {current.linkedTo && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Linked</div>
                  <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>
                    <LinkedRefChip id={current.linkedTo} kind={current.kind === "decision" ? "decision" : current.kind === "batch" ? "batch" : undefined} testId={`tl-linked-${current.id}`} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
